/**
 * MonsterEntityAdapter - Map SRD / legacy / comprehensive-schema monsters
 * into the canonical monster_template.json entity data + metadata.
 *
 * date_of_change: 2025-12-03
 * rule_reference: AGENTS.md SSOT + frontend/src/schemas/monster_template.json
 * purpose: Provide a single, reusable adapter that knows how to build
 *          schema-compliant monster entities from the various monster
 *          JSON shapes used in the app (SRD 5.1 files, DataService
 *          exports, and near-canonical uploads) without relaxing
 *          additionalProperties:false.
 */

/**
 * Build canonical monster entity pieces from a raw monster document.
 *
 * This function intentionally works on the "SRD-style" / comprehensive
 * schema shape used in frontend/src/data/monsters/*.json and
 * MonsterSchema.js:
 *   - root.{schemaVersion,type,exportDate,templateId,id,source,data,tags}
 *   - data.{name,size,creatureType,alignment,cr,ac,hp,speed,abilities,
 *           skills,senses,languages,traits,actions}
 *
 * It also tolerates inputs that are closer to the canonical
 * monster_template.json data shape by falling back to nested fields where
 * possible.
 *
 * @param {Object} raw - Raw monster JSON (SRD/comprehensive/legacy)
 * @param {Object} options
 * @param {string} [options.now] - ISO timestamp used for metadata
 * @param {string} [options.defaultId] - Fallback id if raw.id is missing
 * @param {boolean} [options.markAsTemplate] - Tag as built-in template
 * @param {boolean} [options.markAsUpload] - Tag as uploaded monster
 * @returns {{ id: string, data: Object, metadata: Object }}
 */
export function buildMonsterEntityFromRaw(raw, options = {}) {
  const { now, defaultId, markAsTemplate = false, markAsUpload = false } = options;
  const timestamp = typeof now === 'string' && now.trim().length ? now : new Date().toISOString();

  const srd = raw || {};
  const src = srd.data || srd || {};

  const toFt = (n) => {
    if (n == null) return undefined;
    if (typeof n === 'string') {
      const m = n.match(/\d+/);
      if (!m) return undefined;
      return `${parseInt(m[0], 10)} ft`;
    }
    const v = Number(n);
    return Number.isFinite(v) ? `${v} ft` : undefined;
  };

  const lowerSize = (s) => String(s || 'Medium').toLowerCase();

  const longAbilities = (a) => {
    const srcA = a || src.abilities || src.stats || {};
    return {
      strength: Number(srcA.str ?? srcA.strength ?? 10),
      dexterity: Number(srcA.dex ?? srcA.dexterity ?? 10),
      constitution: Number(srcA.con ?? srcA.constitution ?? 10),
      intelligence: Number(srcA.int ?? srcA.intelligence ?? 10),
      wisdom: Number(srcA.wis ?? srcA.wisdom ?? 10),
      charisma: Number(srcA.cha ?? srcA.charisma ?? 10)
    };
  };

  const mapSkills = (skills) => {
    const rawSkills = skills || src.skills || src.data?.skills;
    if (!rawSkills || typeof rawSkills !== 'object') return undefined;
    const out = {};
    for (const [k, v] of Object.entries(rawSkills)) {
      if (v && typeof v === 'object' && typeof v.bonus === 'number') out[k] = v.bonus;
      else if (typeof v === 'number') out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  };

  const mapTraits = (traits) => {
    const rawTraits = traits || src.traits || src.data?.traits;
    if (!Array.isArray(rawTraits)) return [];
    return rawTraits.map((t) => ({
      ...t,
      name: String(t.name || 'Trait'),
      text: String(t.text || t.description || '')
    }));
  };

  const mapActions = (actions, defaultActivationType = 'action') => {
    // Callers pass the exact canonical economy bucket. Falling back to the
    // main action list here would clone ordinary actions into empty bonus and
    // reaction buckets, falsely changing their costs.
    // rule_reference: D&D 5e action economy; date_of_change: 2026-09-02
    const rawActions = Array.isArray(actions) ? actions : [];
    if (!Array.isArray(rawActions)) return [];

    return rawActions.map((a) => {
      const atk = a.attack || {};
      const kind = a.kind || (() => {
        switch (atk.type) {
          case 'meleeWeapon': return 'meleeWeaponAttack';
          case 'rangedWeapon': return 'rangedWeaponAttack';
          case 'meleeSpell':   return 'meleeSpellAttack';
          case 'rangedSpell':  return 'rangedSpellAttack';
          default: return 'ability';
        }
      })();

      const dmg = Array.isArray(atk.damage)
        ? atk.damage.map((d) => ({
            dice: String(d.dice || d.formula || ''),
            type: String(d.type || '').toLowerCase()
          }))
        : [];

      const reach = atk.reach != null ? toFt(atk.reach) : undefined;
      const range = atk.range && (atk.range.normal || atk.range.long)
        ? {
            short: atk.range.normal != null ? toFt(atk.range.normal) : undefined,
            long: atk.range.long != null ? toFt(atk.range.long) : undefined
          }
        : undefined;

      const out = {
        ...a,
        name: String(a.name || 'Action'),
        kind,
        standardActionKey: a.standardActionKey ? String(a.standardActionKey) : undefined,
        activationType: a.activationType ? String(a.activationType) : defaultActivationType,
        actionType: a.actionType ? String(a.actionType) : undefined,
        type: a.type ? String(a.type) : undefined,
        executionRoute: a.executionRoute ? String(a.executionRoute) : undefined,
        targetRequirement: a.targetRequirement ? String(a.targetRequirement) : undefined,
        ruleReference: a.ruleReference ? String(a.ruleReference) : undefined,
        toHit: typeof atk.toHit === 'number' ? atk.toHit : undefined,
        reach,
        range,
        damage: dmg.length ? dmg : undefined,
        text: typeof a.text === 'string' ? a.text : (atk.notes ? String(atk.notes) : (a.description ? String(a.description) : undefined)),
        // Preserve the explicit runtime route carried by canonical utility
        // abilities. Display names and prose must never be parsed to recover
        // an action's resolver or economy behavior.
        // rule_reference: AGENTS.md — no heuristic inference of rules at runtime.
        // date_of_change: 2026-09-02
        featureKey: a.featureKey ? String(a.featureKey) : undefined
      };

      // Prune undefined keys to satisfy additionalProperties:false
      Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
      if (out.range && out.range.short === undefined && out.range.long === undefined) delete out.range;
      return out;
    });
  };

  const movement = (() => {
    const m = {};
    const speed = src.speed || src.data?.speed || {};
    if (speed.walk != null) m.walk = toFt(speed.walk);
    if (speed.fly != null) m.fly = toFt(speed.fly);
    if (speed.climb != null) m.climb = toFt(speed.climb);
    if (speed.swim != null) m.swim = toFt(speed.swim);
    if (speed.burrow != null) m.burrow = toFt(speed.burrow);
    Object.keys(m).forEach((k) => !m[k] && delete m[k]);
    return m;
  })();

  const senses = (() => {
    const srcSenses = src.senses || src.data?.senses || {};
    const s = {};
    if (srcSenses.darkvision != null) s.darkvision = toFt(srcSenses.darkvision);
    if (srcSenses.blindsight != null) s.blindsight = toFt(srcSenses.blindsight);
    if (srcSenses.tremorsense != null) s.tremorsense = toFt(srcSenses.tremorsense);
    if (srcSenses.truesight != null) s.truesight = toFt(srcSenses.truesight);
    if (srcSenses.passivePerception != null) s.passivePerception = Number(srcSenses.passivePerception);
    return Object.keys(s).length ? s : undefined;
  })();

  const languages = (() => {
    const rawLang = src.languages || src.data?.languages;
    if (!rawLang) return undefined;
    if (Array.isArray(rawLang.list)) {
      return rawLang.list.length ? { spoken: rawLang.list.slice() } : undefined;
    }
    if (Array.isArray(rawLang)) {
      return rawLang.length ? { spoken: rawLang.slice() } : undefined;
    }
    if (typeof rawLang === 'string') {
      const parts = rawLang.split(/[;,]+/).map((p) => p.trim()).filter(Boolean);
      return parts.length ? { spoken: parts } : undefined;
    }
    return undefined;
  })();

  const crDisplay = (() => {
    const cr = src.cr || src.data?.cr || {};
    if (typeof cr === 'string' || typeof cr === 'number') return cr;
    if (cr.display != null) return cr.display;
    if (cr.value != null) return cr.value;
    return '1/4';
  })();

  const canonical = {
    // Preserve authored mechanics at intake. SRD 5.1; date_of_change: 2026-09-05.
    contentVersion: src.contentVersion,
    resistanceBlock: src.resistanceBlock,
    conditionImmunities: src.conditionImmunities,
    savingThrows: src.savingThrows,
    equipmentState: src.equipmentState,
    name: src.name,
    taxonomy: {
      size: lowerSize(src.size || src.taxonomy?.size || 'Medium'),
      type: src.creatureType?.type || src.creature_type?.type || src.taxonomy?.type || src.monsterType || 'humanoid',
      subtypes: Array.isArray(src.creatureType?.tags) ? src.creatureType.tags : src.taxonomy?.subtypes,
      alignment: src.alignment || src.taxonomy?.alignment || undefined
    },
    abilities: longAbilities(src.abilities || src.stats),
    proficiencies: (() => {
      const skills = mapSkills(src.skills);
      return skills ? { skills } : undefined;
    })(),
    defense: {
      ac: {
        base: (() => {
          if (typeof src.ac === 'number') return Number(src.ac);
          if (src.ac && typeof src.ac.value === 'number') return Number(src.ac.value);
          if (src.defense && src.defense.ac && typeof src.defense.ac.base === 'number') return Number(src.defense.ac.base);
          return 10;
        })(),
        ...(src.ac?.notes ? { notes: String(src.ac.notes) } : (src.defense?.ac?.notes ? { notes: String(src.defense.ac.notes) } : {}))
      },
      hp: (() => {
        const average = src.hp && typeof src.hp.average === 'number' ? src.hp.average : undefined;
        const numeric = typeof src.hp === 'number' ? src.hp : undefined;
        const defMax = src.defense?.hp?.max;
        const defCurrent = src.defense?.hp?.current;
        const max = Number(
          (average != null ? average : (numeric != null ? numeric : defMax)) || 1
        );
        const current = Number(
          (average != null ? average : (numeric != null ? numeric : (defCurrent != null ? defCurrent : defMax))) || 1
        );
        const hitDice = String((src.hp && src.hp.formula) || src.defense?.hp?.hitDice || '1d8');
        return { max, current, hitDice };
      })()
    },
    movement,
    senses,
    languages,
    // date_of_change: 2025-12-04 - Compatibility: emit only CR; pb/xp derived at runtime to avoid additionalProperties in older schemas
    challenge: {
      cr: crDisplay
    },
    // date_of_change: 2025-12-02 - Move artwork into canonical identity.image (optional) to satisfy schema
    identity: (srd._meta?.artwork || src.images?.artwork || src.image || src.imageUrl)
      ? { image: String(srd._meta?.artwork || src.images?.artwork || src.image || src.imageUrl) }
      : undefined,
    traits: mapTraits(src.traits),
    actions: mapActions(src.actions || src.data?.actions),
    // Action, bonus-action, and reaction buckets are separate canonical
    // contracts. Dropping either secondary bucket makes valid monster
    // abilities disappear from BattleRoom's action economy.
    // rule_reference: D&D 5e action economy; date_of_change: 2026-09-02
    bonusActions: mapActions(src.bonusActions || src.data?.bonusActions, 'bonus action'),
    reactions: mapActions(src.reactions || src.data?.reactions, 'reaction')
  };

  // date_of_change: 2025-12-04
  // rule_reference: monster_template.json additionalProperties:false — Strictly prune unknown/undefined keys at every level
  const pruneKeys = (obj, allowed) => {
    if (!obj || typeof obj !== 'object') return obj;
    for (const key of Object.keys(obj)) {
      if (obj[key] === undefined || (allowed && !allowed.has(key))) {
        delete obj[key];
      }
    }
    return obj;
  };

  const allowed = {
    data: new Set(['contentVersion','resistanceBlock','conditionImmunities','savingThrows','equipmentState','name','aliases','taxonomy','abilities','proficiencies','defense','movement','senses','languages','challenge','identity','traits','actions','bonusActions','reactions','legendary','mythic','lair','spellcasting','innateSpellcasting','equipment','loot','ai','ascendant']),
    taxonomy: new Set(['size','type','subtypes','alignment','tags']),
    abilities: new Set(['strength','dexterity','constitution','intelligence','wisdom','charisma']),
    proficiencies: new Set(['savingThrows','skills','tools']),
    savingThrows: new Set(['strength','dexterity','constitution','intelligence','wisdom','charisma']),
    defense: new Set(['ac','hp','damageModifiers','conditionImmunities','legendaryResistancesPerDay','exhaustionImmunity']),
    ac: new Set(['base','notes','calc']),
    hp: new Set(['max','current','temporary','hitDice']),
    movement: new Set(['walk','fly','hover','climb','swim','burrow','notes']),
    senses: new Set(['blindsight','darkvision','tremorsense','truesight','passivePerception']),
    languages: new Set(['spoken','telepathy','understandsButCantSpeak']),
    challenge: new Set(['cr','pb','xp']),
    identity: new Set(['description','lore','image','tokens','source']),
    trait: new Set(['id','mechanics','activation','ruleReference','name','text','tags','recharge','aura']),
    action: new Set(['id','mechanics','activationType','actionType','type','attack','name','kind','cost','reach','range','targets','toHit','save','damage','onHit','onMiss','aoe','recharge','uses','components','concentration','text','parts','standardActionKey','executionRoute','targetRequirement','ruleReference','featureKey']),
    actionRange: new Set(['short','long']),
    actionSave: new Set(['ability','dc','onSuccess','onFailure']),
    damageItem: new Set(['dice','type','magical','notes'])
  };

  // Prune top-level optional sections and unknowns
  pruneKeys(canonical, allowed.data);

  // Prune nested known objects
  if (canonical.taxonomy) pruneKeys(canonical.taxonomy, allowed.taxonomy);
  if (canonical.abilities) pruneKeys(canonical.abilities, allowed.abilities);
  if (canonical.proficiencies) {
    pruneKeys(canonical.proficiencies, allowed.proficiencies);
    if (canonical.proficiencies.savingThrows) pruneKeys(canonical.proficiencies.savingThrows, allowed.savingThrows);
  }
  if (canonical.defense) {
    pruneKeys(canonical.defense, allowed.defense);
    if (canonical.defense.ac) pruneKeys(canonical.defense.ac, allowed.ac);
    if (canonical.defense.hp) pruneKeys(canonical.defense.hp, allowed.hp);
    if (Array.isArray(canonical.defense.damageModifiers)) {
      canonical.defense.damageModifiers = canonical.defense.damageModifiers.map((dm) => pruneKeys(dm, new Set(['kind','damageType','qualifiers','notes'])));
    }
  }
  if (canonical.movement) pruneKeys(canonical.movement, allowed.movement);
  if (canonical.senses) pruneKeys(canonical.senses, allowed.senses);
  if (canonical.languages) pruneKeys(canonical.languages, allowed.languages);
  if (canonical.challenge) pruneKeys(canonical.challenge, allowed.challenge);
  if (canonical.identity) pruneKeys(canonical.identity, allowed.identity);

  if (Array.isArray(canonical.traits)) {
    canonical.traits = canonical.traits.map((t) => pruneKeys(t, allowed.trait));
  }
  const pruneActionBucket = (entries) => {
    if (!Array.isArray(entries)) return entries;
    return entries.map((a) => {
      pruneKeys(a, allowed.action);
      if (a.range) pruneKeys(a.range, allowed.actionRange);
      if (a.save) pruneKeys(a.save, allowed.actionSave);
      if (Array.isArray(a.damage)) {
        a.damage = a.damage.map((d) => pruneKeys(d, allowed.damageItem));
      }
      return a;
    });
  };
  canonical.actions = pruneActionBucket(canonical.actions);
  canonical.bonusActions = pruneActionBucket(canonical.bonusActions);
  canonical.reactions = pruneActionBucket(canonical.reactions);

  // Final pass: remove any undefined leftovers everywhere we explicitly touched
  Object.keys(canonical).forEach((k) => canonical[k] === undefined && delete canonical[k]);
  if (canonical.taxonomy) Object.keys(canonical.taxonomy).forEach((k) => canonical.taxonomy[k] === undefined && delete canonical.taxonomy[k]);
  if (canonical.proficiencies) {
    Object.keys(canonical.proficiencies).forEach((k) => canonical.proficiencies[k] === undefined && delete canonical.proficiencies[k]);
    if (canonical.proficiencies.savingThrows) Object.keys(canonical.proficiencies.savingThrows).forEach((k) => canonical.proficiencies.savingThrows[k] === undefined && delete canonical.proficiencies.savingThrows[k]);
  }
  if (canonical.defense) {
    if (canonical.defense.ac) Object.keys(canonical.defense.ac).forEach((k) => canonical.defense.ac[k] === undefined && delete canonical.defense.ac[k]);
    if (canonical.defense.hp) Object.keys(canonical.defense.hp).forEach((k) => canonical.defense.hp[k] === undefined && delete canonical.defense.hp[k]);
  }
  if (canonical.movement) Object.keys(canonical.movement).forEach((k) => canonical.movement[k] === undefined && delete canonical.movement[k]);
  if (canonical.senses) Object.keys(canonical.senses).forEach((k) => canonical.senses[k] === undefined && delete canonical.senses[k]);
  if (canonical.languages) Object.keys(canonical.languages).forEach((k) => canonical.languages[k] === undefined && delete canonical.languages[k]);
  if (canonical.challenge) Object.keys(canonical.challenge).forEach((k) => canonical.challenge[k] === undefined && delete canonical.challenge[k]);
  if (canonical.identity) Object.keys(canonical.identity).forEach((k) => canonical.identity[k] === undefined && delete canonical.identity[k]);

  // Build metadata with SRD / upload labeling but only using schema-allowed fields
  const tags = [];
  if (markAsTemplate) tags.push('template');
  if (markAsUpload) tags.push('upload');

  const srcTags = Array.isArray(srd.tags) ? srd.tags : [];
  if (srcTags.includes('srd-5e')) tags.push('srd-5e');

  const srcSource = srd.source || src.source || {};
  const sourceName = srcSource.name ? String(srcSource.name) : '';
  const sourceEdition = srcSource.edition ? String(srcSource.edition) : '';
  const sourceLicense = srcSource.license ? String(srcSource.license) : '';

  const parts = [sourceName, sourceEdition, sourceLicense].filter(Boolean);
  const sourceLabel = parts.join(' | ');

  const metadata = {
    created: timestamp,
    modified: timestamp,
    ...(tags.length ? { tags: Array.from(new Set(tags)) } : {}),
    ...(sourceLabel ? { notes: `Source: ${sourceLabel}` } : {})
  };

  // Prune any undefined metadata fields just in case
  Object.keys(metadata).forEach((k) => metadata[k] === undefined && delete metadata[k]);

  const id = srd.id || defaultId || (src.name ? String(src.name).toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined) || `monster_${Date.now()}`;

  return { id, data: canonical, metadata };
}
